import UserService from "./UserService";
import ConfigurationService from "./ConfigurationService";
import Kichiri from "./KichiriService";

const LoanReconciliationService = {
	/**
	 * Fetches the client's QBO liability (loan) accounts eligible for reconciliation.
	 * @param {Object} params
	 * @param {string|number} params.clientId - The client whose loan accounts to list.
	 * @returns {Promise<Array>} The list of loan accounts, or an empty array on error.
	 */
	async fetchLoanAccounts({ clientId }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.loanReconAccounts({}, { clientId }, userData.authToken);
			return response.data.accounts || [];
		} catch (error) {
			console.error("Error fetching loan accounts:", error);
			return [];
		}
	},

	/**
	 * Runs a loan reconciliation by uploading a loan statement PDF along with the
	 * account and period details. Uses a raw multipart fetch since Kichiri doesn't
	 * support file uploads.
	 * @param {Object} params
	 * @param {string|number} params.clientId - The client being reconciled.
	 * @param {string|number} params.accountId - QBO loan account ID.
	 * @param {string} params.accountName - Display name of the loan account.
	 * @param {string} params.startDate - Reconciliation period start (YYYY-MM-DD).
	 * @param {string} params.endDate - Reconciliation period end (YYYY-MM-DD).
	 * @param {File} params.file - The loan statement PDF to upload.
	 * @param {string} [params.cashAccountName] - Optional cash/bank account name for JE suggestions.
	 * @returns {Promise<Object>} The reconciliation result, or an object with an `error` field on failure.
	 */
	async runReconciliation({ clientId, accountId, accountName, startDate, endDate, file, cashAccountName }) {
		let userData = UserService.getUserData();

		try {
			// Multipart upload — kichiri doesn't support multipart, stays on raw fetch.
			let formData = new FormData();
			formData.append("files", file, file.name);
			formData.append("clientId", clientId);
			formData.append("accountId", accountId);
			formData.append("accountName", accountName);
			formData.append("startDate", startDate);
			formData.append("endDate", endDate);
			if (cashAccountName) formData.append("cashAccountName", cashAccountName);

			let response = await fetch(
				`${ConfigurationService.resolveApi().rest}/tools/loanReconciliation/run`,
				{
					method: "POST",
					body: formData,
					headers: { authorization: userData.authToken }
				}
			);

			if (response.status !== 200) {
				let errorData = await response.json().catch(() => ({}));
				return { error: errorData.error || "Failed to run loan reconciliation" };
			}

			return await response.json();
		} catch (error) {
			console.error("Error running loan reconciliation:", error);
			return { error: "Failed to run loan reconciliation" };
		}
	},

	/**
	 * Fetches the history of past loan reconciliations for a client.
	 * @param {Object} params
	 * @param {string|number} params.clientId - The client whose reconciliation history to list.
	 * @returns {Promise<Array>} The list of reconciliations, or an empty array on error.
	 */
	async listReconciliations({ clientId }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.loanReconHistory({}, { clientId }, userData.authToken);
			return response.data.reconciliations || [];
		} catch (error) {
			console.error("Error fetching loan reconciliation history:", error);
			return [];
		}
	},

	/**
	 * Retrieves the active amortization schedule for a loan account, including its
	 * rows and reconciliation history.
	 * @param {Object} params
	 * @param {string|number} params.clientId - The client that owns the loan.
	 * @param {string|number} params.loanAccountId - QBO loan account ID.
	 * @returns {Promise<Object>} An object with `schedule`, `rows`, and `history` (empty defaults on error).
	 */
	async getSchedule({ clientId, loanAccountId }) {
		let userData = UserService.getUserData();
		try {
			let response = await Kichiri.tools.loanReconScheduleGet(
				{},
				{ clientId, loanAccountId },
				userData.authToken
			);
			return response.data;
		} catch (error) {
			console.error("Error fetching schedule:", error);
			return { schedule: null, rows: [], history: [] };
		}
	},

	/**
	 * Generates a new amortization schedule from loan terms (principal, rate, term, etc.).
	 * @param {Object} payload - The loan terms used to compute the schedule.
	 * @returns {Promise<Object>} The generated schedule, or an object with an `error` field on failure.
	 */
	async generateSchedule(payload) {
		let userData = UserService.getUserData();
		try {
			let response = await Kichiri.tools.loanReconScheduleGenerate(payload, {}, userData.authToken);
			return response.data;
		} catch (error) {
			console.error("Error generating schedule:", error);
			let errorMessage = error?.response?.data?.error || "Failed to generate schedule";
			return { error: errorMessage };
		}
	},

	/**
	 * Uploads an amortization schedule file (e.g. a lender-provided PDF/spreadsheet)
	 * to create the schedule for a loan account. Uses a raw multipart fetch.
	 * @param {Object} params
	 * @param {string|number} params.clientId - The client that owns the loan.
	 * @param {string|number} params.loanAccountId - QBO loan account ID.
	 * @param {string} [params.loanAccountName] - Optional display name of the loan account.
	 * @param {File} params.file - The schedule file to upload.
	 * @param {string} [params.paymentFrequency] - Optional payment frequency (e.g. monthly, biweekly).
	 * @returns {Promise<Object>} The parsed schedule, or an object with an `error` field on failure.
	 */
	async uploadSchedule({ clientId, loanAccountId, loanAccountName, file, paymentFrequency }) {
		let userData = UserService.getUserData();
		try {
			// Multipart upload — kichiri doesn't support multipart, stays on raw fetch.
			let formData = new FormData();
			formData.append("files", file, file.name);
			formData.append("clientId", clientId);
			formData.append("loanAccountId", loanAccountId);
			if (loanAccountName) formData.append("loanAccountName", loanAccountName);
			if (paymentFrequency) formData.append("paymentFrequency", paymentFrequency);

			let response = await fetch(
				`${ConfigurationService.resolveApi().rest}/tools/loanReconciliation/schedule/upload`,
				{
					method: "POST",
					body: formData,
					headers: { authorization: userData.authToken }
				}
			);
			if (response.status !== 200) {
				const err = await response.json().catch(() => ({}));
				return { error: err.error || "Failed to upload schedule" };
			}
			return await response.json();
		} catch (error) {
			console.error("Error uploading schedule:", error);
			return { error: "Failed to upload schedule" };
		}
	},

	/**
	 * Replaces an existing amortization schedule from a given date forward by
	 * uploading a new schedule file, preserving rows before `replaceFromDate`.
	 * Uses a raw multipart fetch.
	 * @param {Object} params
	 * @param {string|number} params.clientId - The client that owns the loan.
	 * @param {string|number} params.loanAccountId - QBO loan account ID.
	 * @param {string} [params.loanAccountName] - Optional display name of the loan account.
	 * @param {File} params.file - The replacement schedule file to upload.
	 * @param {string} [params.replaceFromDate] - Date (YYYY-MM-DD) from which to replace schedule rows.
	 * @returns {Promise<Object>} The updated schedule, or an object with an `error` field on failure.
	 */
	async replaceSchedule({ clientId, loanAccountId, loanAccountName, file, replaceFromDate }) {
		let userData = UserService.getUserData();
		try {
			// Multipart upload — kichiri doesn't support multipart, stays on raw fetch.
			let formData = new FormData();
			formData.append("files", file, file.name);
			formData.append("clientId", clientId);
			formData.append("loanAccountId", loanAccountId);
			if (loanAccountName) formData.append("loanAccountName", loanAccountName);
			if (replaceFromDate) formData.append("replaceFromDate", replaceFromDate);

			let response = await fetch(
				`${ConfigurationService.resolveApi().rest}/tools/loanReconciliation/schedule/replace`,
				{
					method: "POST",
					body: formData,
					headers: { authorization: userData.authToken }
				}
			);
			if (response.status !== 200) {
				const err = await response.json().catch(() => ({}));
				return { error: err.error || "Failed to replace schedule" };
			}
			return await response.json();
		} catch (error) {
			console.error("Error replacing schedule:", error);
			return { error: "Failed to replace schedule" };
		}
	},

	/**
	 * Recalculates an existing schedule (e.g. after a rate change or extra payment).
	 * @param {Object} payload - The parameters driving the recalculation.
	 * @returns {Promise<Object>} The recalculated schedule, or an object with an `error` field on failure.
	 */
	async recalculateSchedule(payload) {
		let userData = UserService.getUserData();
		try {
			let response = await Kichiri.tools.loanReconScheduleRecalculate(payload, {}, userData.authToken);
			return response.data;
		} catch (error) {
			console.error("Error recalculating schedule:", error);
			let errorMessage = error?.response?.data?.error || "Failed to recalculate";
			return { error: errorMessage };
		}
	},

	/**
	 * Deletes/resets the amortization schedule for a loan account.
	 * @param {Object} params
	 * @param {string|number} params.clientId - The client that owns the loan.
	 * @param {string|number} params.loanAccountId - QBO loan account ID.
	 * @returns {Promise<Object>} The reset result, or an object with an `error` field on failure.
	 */
	async resetSchedule({ clientId, loanAccountId }) {
		let userData = UserService.getUserData();
		try {
			let response = await Kichiri.tools.loanReconScheduleReset(
				{ clientId, loanAccountId },
				{},
				userData.authToken
			);
			return response.data;
		} catch (error) {
			console.error("Error resetting schedule:", error);
			let errorMessage = error?.response?.data?.error || "Failed to reset schedule";
			return { error: errorMessage };
		}
	},

	/**
	 * Updates a single amortization schedule row's status and/or notes.
	 * @param {Object} params
	 * @param {string|number} params.rowId - The schedule row to update.
	 * @param {string} params.status - The new status for the row (e.g. paid, missed).
	 * @param {string} params.notes - Free-text notes for the row.
	 * @returns {Promise<Object>} The updated row, or an object with an `error` field on failure.
	 */
	async updateScheduleRow({ rowId, status, notes }) {
		let userData = UserService.getUserData();
		try {
			let response = await Kichiri.tools.loanReconScheduleUpdateRow(
				{ rowId, status, notes },
				{},
				userData.authToken
			);
			return response.data;
		} catch (error) {
			console.error("Error updating row:", error);
			return { error: "Failed to update row" };
		}
	},

	/**
	 * Fetches a month-by-month overview of a loan's schedule for a given year.
	 * @param {Object} params
	 * @param {string|number} params.clientId - The client that owns the loan.
	 * @param {string|number} params.loanAccountId - QBO loan account ID.
	 * @param {string|number} params.year - The calendar year to summarize.
	 * @returns {Promise<Object>} An object with `months` and `totals` (empty defaults on error).
	 */
	async getYearOverview({ clientId, loanAccountId, year }) {
		let userData = UserService.getUserData();
		try {
			let response = await Kichiri.tools.loanReconScheduleYear(
				{},
				{ clientId, loanAccountId, year },
				userData.authToken
			);
			return response.data;
		} catch (error) {
			console.error("Error fetching year overview:", error);
			return { months: [], totals: {} };
		}
	}
};

export default LoanReconciliationService;
